import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  IconButton,
  Paper,
  Divider,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Card,
  CardContent,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  alpha,
  useTheme
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  AttachFile as AttachFileIcon,
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useAppContext } from './GrowthSessionManagement';
import { updateGrowthSession as updateGrowthSessionAPI, uploadFile } from '../Queries';

const PIPDialog = ({
  open,
  onClose,
  growthSession = null,
  growthSessionId = null,
  userId = null,
  onSuccess
}) => {
  const { updateGrowthSession, users, getUsersByRole, growthSessions } = useAppContext();
  const theme = useTheme();
  const [expandedPanel, setExpandedPanel] = useState('panel0');
  const [pipForm, setPipForm] = useState({
    final_decision: '',
    location: '',
    release_date: '',
    pip_start_date: new Date().toISOString().split('T')[0],
    pip_end_date: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    attached_files: [],
    dynamic_fields: {}
  });

  const [attachedFiles, setAttachedFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [dynamicFields, setDynamicFields] = useState({});
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newFieldValue, setNewFieldValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewFile, setPreviewFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Debug: Watch attachedFiles changes
  useEffect(() => {
    console.log('attachedFiles state changed:', attachedFiles);
  }, [attachedFiles]);

  const currentUser = localStorage.getItem("user");
  const finalDecisionOptions = ['Continue Employment', 'Terminate Employment', 'Extended PIP', 'Role Change'];
  const usersList = getUsersByRole('User');
  const managersList = getUsersByRole('Manager');
  const leadsList = getUsersByRole('Lead');

  // Function to get user details by id or email
  const getUserDetails = (userIdOrEmail) => {
    if (!userIdOrEmail || !users) return null;
    return users.find(u => u.id === userIdOrEmail || u.email === userIdOrEmail);
  };

  // Get the actual Growth Conversation object
  const actualGrowthSession = growthSession || (growthSessionId ? growthSessions?.find(gs => gs.id === growthSessionId) : null);

  // Get data from the Growth Conversation first
  const actualUserId = userId || actualGrowthSession?.user_id || '';
  const actualGrowthSessionId = actualGrowthSession?.id || growthSessionId || '';

  // Get the selected user data
  const selectedUser = getUserDetails(actualUserId);

  // Get manager and lead with fallback to user profile (like GrowthSessionDialog does)
  const actualManagerId = actualGrowthSession?.manager_id || selectedUser?.assignedManager || '';
  const actualLeadId = actualGrowthSession?.lead_id || selectedUser?.assignedLead || '';

  console.log('[PIPDialog] Growth Conversation Data:', {
    actualGrowthSession,
    selectedUser,
    actualUserId,
    actualManagerId,
    actualLeadId,
    userId,
    growthSessionId
  });

  const steps = [
    'Basic Information',
    'PIP Decision',
    'Documentation',
    'Review & Finalize'
  ];

  // Function to get user by email (similar to GrowthSessionManagement)
  const getUserByEmail = (email) => {
    if (!email || !users) return null;
    return users.find(user => user.email === email);
  };

  // Helper functions to get names for manager and lead
  const getManagerName = (managerId) => {
    if (!managerId) return 'Not assigned';

    console.log('[PIPDialog] Looking for manager:', managerId, typeof managerId);
    console.log('[PIPDialog] Available users:', users?.map(u => ({ id: u.id, email: u.email, name: u.name, role: u.role })));

    // Handle different manager ID formats
    let manager = null;

    if (Array.isArray(managerId)) {
      // If it's an array (from user.assignedManager), take the first one
      console.log('[PIPDialog] Manager is array:', managerId);
      if (managerId.length > 0) {
        const firstManagerId = managerId[0];
        manager = getUserByEmail(firstManagerId) || users?.find(u => u.id === firstManagerId);
      }
    } else {
      // If it's a string (email or ID)
      manager = getUserByEmail(managerId) || users?.find(u => u.id === managerId);
    }

    console.log('[PIPDialog] Found manager:', manager);

    return manager ? manager.name : 'Not assigned';
  };

  const getLeadName = (leadId) => {
    if (!leadId) return 'Not assigned';

    console.log('[PIPDialog] Looking for lead:', leadId, typeof leadId);

    // Handle different lead ID formats
    let lead = null;

    if (Array.isArray(leadId)) {
      // If it's an array (from user.assignedLead), take the first one
      console.log('[PIPDialog] Lead is array:', leadId);
      if (leadId.length > 0) {
        const firstLeadId = leadId[0];
        lead = getUserByEmail(firstLeadId) || users?.find(u => u.id === firstLeadId);
      }
    } else {
      // If it's a string (email or ID)
      lead = getUserByEmail(leadId) || users?.find(u => u.id === leadId);
    }

    console.log('[PIPDialog] Found lead:', lead);

    return lead ? lead.name : 'Not assigned';
  };

  // Function to get auto-populated manager and lead data from Growth Conversation
  // const getAutoPopulatedData = () => {
  //   if (growthSessionId && growthSessions) {
  //     const growthSession = growthSessions.find(gs => gs.id === growthSessionId);
  //     if (growthSession) {
  //       // Get manager and lead from Growth Conversation
  //       return {
  //         manager_id: growthSession.manager_id || '',
  //         lead_id: growthSession.lead_id || ''
  //       };
  //     }
  //   }

  //   // If no Growth Conversation data, try to find user's assigned manager/lead from users data
  //   if (userId && users) {
  //     const user = getUserDetails(userId);
  //     if (user) {
  //       return {
  //         manager_id: user.manager_id || user.assignedTo || '',
  //         lead_id: user.lead_id || ''
  //       };
  //     }
  //   }

  //   return {
  //     manager_id: '',
  //     lead_id: ''
  //   };
  // };

  // Get selected user name for display
  const selectedUserName = selectedUser ? selectedUser.name : '';

  useEffect(() => {
    if (actualGrowthSession && open) {
      const pipData = typeof actualGrowthSession.pip_data === 'string'
        ? JSON.parse(actualGrowthSession.pip_data)
        : actualGrowthSession.pip_data || {};

      setPipForm({
        final_decision: pipData.final_decision || '',
        location: pipData.location || '',
        release_date: pipData.release_date || '',
        pip_start_date: pipData.pip_start_date ? pipData.pip_start_date.split('T')[0] : new Date().toISOString().split('T')[0],
        pip_end_date: pipData.pip_end_date ? pipData.pip_end_date.split('T')[0] : new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        attached_files: pipData.attached_files || [],
        dynamic_fields: pipData.dynamic_fields || {}
      });

      setAttachedFiles(pipData.attached_files || []);
      setDynamicFields(pipData.dynamic_fields || {});
    } else if (open) {
      resetForm();
    }
  }, [growthSession, open]);

  const resetForm = () => {
    setPipForm({
      final_decision: '',
      location: '',
      release_date: '',
      pip_start_date: new Date().toISOString().split('T')[0],
      pip_end_date: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      attached_files: [],
      dynamic_fields: {}
    });
    setAttachedFiles([]);
    setDynamicFields({});
    setNewFieldKey('');
    setNewFieldValue('');
    setError('');
  };

  const handlePipDataChange = (field, value) => {
    setPipForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addDynamicField = () => {
    if (newFieldKey.trim() && newFieldValue.trim()) {
      const newDynamicFields = { ...dynamicFields, [newFieldKey.trim()]: newFieldValue.trim() };
      setDynamicFields(newDynamicFields);
      setPipForm(prev => ({
        ...prev,
        dynamic_fields: newDynamicFields
      }));
      setNewFieldKey('');
      setNewFieldValue('');
    }
  };

  const removeDynamicField = (key) => {
    const newDynamicFields = { ...dynamicFields };
    delete newDynamicFields[key];
    setDynamicFields(newDynamicFields);
    setPipForm(prev => ({
      ...prev,
      dynamic_fields: newDynamicFields
    }));
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setSelectedFiles(files);
    setUploadingFiles(true);

    try {
      const uploadedFileUrls = [];

      for (const file of files) {
        // Create file name with _pip suffix
        const fileExtension = file.name.split('.').pop();
        const fileNameWithoutExt = file.name.replace(`.${fileExtension}`, '');
        const pipFileName = `${fileNameWithoutExt}_pip.${fileExtension}`;

        // Convert to base64
        const base64Data = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = reader.result.split(',')[1]; // Remove data:type;base64, prefix
            resolve(base64);
          };
          reader.readAsDataURL(file);
        });

        // Upload file using uploadFile API
        const response = await uploadFile(pipFileName, base64Data, 'documents');

        console.log('Upload response for', pipFileName, ':', response);

        // Parse the response structure: {statusCode: 200, body: "{\"message\":\"...\",\"fileURL\":\"...\"}"}
        let fileUrl = null;
        if (response && response.data.statusCode === 200 && response.data.body) {
          try {
            const parsedBody = JSON.parse(response.data.body);
            fileUrl = parsedBody.fileURL;
          } catch (parseError) {
            console.error('Error parsing upload response body:', parseError);
          }
        }

        if (fileUrl) {
          uploadedFileUrls.push({
            name: pipFileName,
            url: fileUrl,
            uploadedAt: new Date().toISOString()
          });
        } else {
          console.warn('Upload failed or no URL returned for:', pipFileName, response);
          // Add the file anyway with a placeholder URL for debugging
          uploadedFileUrls.push({
            name: pipFileName,
            url: null,
            uploadedAt: new Date().toISOString(),
            uploadError: true
          });
        }
      }

      // Update attached files state
      const updatedFiles = [...attachedFiles, ...uploadedFileUrls];
      console.log('Current attachedFiles:', attachedFiles);
      console.log('Uploaded files:', uploadedFileUrls);
      console.log('Updated files to set:', updatedFiles);

      setAttachedFiles(updatedFiles);

      // Update pipForm state
      setPipForm(prev => ({
        ...prev,
        attached_files: updatedFiles
      }));

      // Clear selected files
      setSelectedFiles([]);
      // Reset file input
      const fileInput = document.getElementById('pip-file-input');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Error uploading files:', error);
      setError(`Error uploading files: ${error.message}`);
    } finally {
      setUploadingFiles(false);
    }
  };



  const removeAttachedFile = (index) => {
    const newFiles = attachedFiles.filter((_, i) => i !== index);
    setAttachedFiles(newFiles);
    handlePipDataChange('attached_files', newFiles);
  };

  const handlePreviewFile = (file) => {
    setPreviewFile(file);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewFile(null);
    setPreviewOpen(false);
  };

  // Removed handleNext and handleBack as we're using accordions now

  const validateStep = (step) => {
    switch (step) {
      case 0:
        return pipForm.user_id && pipForm.pip_data.pip_start_date && pipForm.pip_data.pip_end_date;
      case 1:
        return pipForm.pip_data.final_decision.trim() !== '';
      case 2:
        return pipForm.pip_data.location.trim() !== '';
      case 3:
        return true;
      default:
        return false;
    }
  };

  // Validation function for mandatory fields
  const validateMandatoryFields = () => {
    const errors = [];

    if (!actualUserId) {
      errors.push('Employee selection is required');
    }
    if (!actualManagerId) {
      errors.push('Manager selection is required');
    }
    if (!pipForm.pip_start_date) {
      errors.push('PIP Start Date is required');
    }
    if (!pipForm.pip_end_date) {
      errors.push('PIP End Date is required');
    }
    if (!pipForm.final_decision) {
      errors.push('Final Decision is required');
    }

    return errors;
  };

  // Check if all mandatory fields are filled
  const isFormValid = () => {
    return actualUserId &&
      actualManagerId &&
      pipForm.pip_start_date &&
      pipForm.pip_end_date &&
      pipForm.final_decision;
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');

      // Validate mandatory fields
      const validationErrors = validateMandatoryFields();
      if (validationErrors.length > 0) {
        setError(`Please fill all mandatory fields:\n${validationErrors.join('\n')}`);
        setLoading(false);
        return;
      }

      // Update Growth Conversation with PIP data and change status to pip_created
      const updatedGrowthSession = {
        ...actualGrowthSession,
        pip_data: pipForm,
        status: 'pip_created',
        updated_at: new Date().toISOString()
      };

      await updateGrowthSessionAPI({
        id: actualGrowthSession.id,
        user_id: actualGrowthSession.user_id,
        session_data: actualGrowthSession.session_data,
        manager_comments: actualGrowthSession.manager_comments,
        pip_data: pipForm,
        status: 'pip_created',
        updated_at: updatedGrowthSession.updated_at,
        review_period_from: actualGrowthSession.review_period_from,
        review_period_to: actualGrowthSession.review_period_to,
        date: actualGrowthSession.date,
      });

      // Update local state
      updateGrowthSession(actualGrowthSession.id, updatedGrowthSession);

      onSuccess?.('updated');
      onClose();
    } catch (error) {
      console.error('Error saving PIP plan:', error);
      setError('An error occurred while saving. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info" icon={<AssignmentIcon />} sx={{ fontSize: '0.875rem' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                  PIP Plan Basic Information
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  Set up the basic details for the Performance Improvement Plan.
                  {selectedUserName && (
                    <><br />
                      <strong>Creating PIP for:</strong> {selectedUserName}
                    </>
                  )}
                </Typography>
              </Alert>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Employee"
                value={selectedUserName}
                disabled
                InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                sx={{
                  fontSize: '0.875rem',
                  '& .MuiInputBase-input': { fontSize: '0.875rem' }
                }}
                helperText="From Growth Conversation"
                FormHelperTextProps={{ sx: { fontSize: '0.75rem' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Manager"
                value={getManagerName(actualManagerId)}
                disabled
                InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                sx={{
                  fontSize: '0.875rem',
                  '& .MuiInputBase-input': { fontSize: '0.875rem' }
                }}
                helperText="From Growth Conversation"
                FormHelperTextProps={{ sx: { fontSize: '0.75rem' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Assigned Lead"
                value={getLeadName(actualLeadId)}
                disabled
                InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                sx={{
                  fontSize: '0.875rem',
                  '& .MuiInputBase-input': { fontSize: '0.875rem' }
                }}
                helperText="From Growth Conversation"
                FormHelperTextProps={{ sx: { fontSize: '0.75rem' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="PIP Start Date *"
                value={pipForm.pip_start_date}
                onChange={(e) => handlePipDataChange('pip_start_date', e.target.value)}
                InputLabelProps={{ shrink: true, sx: { fontSize: '0.8rem' } }}
                InputProps={{ sx: { fontSize: '0.8rem' } }}
                error={!pipForm.pip_start_date}
                helperText={!pipForm.pip_start_date ? "Start date is required" : ""}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="PIP End Date *"
                value={pipForm.pip_end_date}
                onChange={(e) => handlePipDataChange('pip_end_date', e.target.value)}
                InputLabelProps={{ shrink: true, sx: { fontSize: '0.8rem' } }}
                InputProps={{ sx: { fontSize: '0.8rem' } }}
                error={!pipForm.pip_end_date}
                helperText={!pipForm.pip_end_date ? "End date is required" : ""}
                required
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="warning" icon={<WarningIcon />} sx={{ fontSize: '0.875rem' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                  PIP Final Decision
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  Document the final decision regarding the employee's performance improvement plan.
                </Typography>
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel sx={{ fontSize: '0.875rem' }}>
                  Final Decision <span style={{ color: 'red' }}>*</span>
                </InputLabel>
                <Select
                  value={pipForm.final_decision}
                  onChange={(e) => handlePipDataChange('final_decision', e.target.value)}
                  label="Final Decision"
                  sx={{ fontSize: '0.875rem' }}
                  error={!pipForm.final_decision}
                >
                  {finalDecisionOptions.map((decision) => (
                    <MenuItem key={decision} value={decision} sx={{ fontSize: '0.875rem' }}>
                      {decision}
                    </MenuItem>
                  ))}
                </Select>
                {!pipForm.final_decision && (
                  <Typography variant="caption" sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                    Final decision is required
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Release Date"
                value={pipForm.release_date}
                onChange={(e) => handlePipDataChange('release_date', e.target.value)}
                InputLabelProps={{ shrink: true, sx: { fontSize: '0.8rem' } }}
                InputProps={{ sx: { fontSize: '0.8rem' } }}
                FormHelperTextProps={{ sx: { fontSize: '0.75rem' } }}
                helperText="Date when the decision becomes effective (if applicable)"
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info" icon={<AttachFileIcon />} sx={{ fontSize: '0.875rem' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                  Documentation & Location
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  Specify the location and attach relevant documentation for the PIP plan.
                </Typography>
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={pipForm.location}
                onChange={(e) => handlePipDataChange('location', e.target.value)}
                placeholder="Office location, department, or meeting venue"
                InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                InputProps={{ sx: { fontSize: '0.875rem' } }}
                sx={{
                  '& .MuiInputBase-input::placeholder': {
                    fontSize: '0.8rem',
                    opacity: 0.6
                  }
                }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                Document Attachments
              </Typography>

              {/* File Upload Section */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
                <Typography variant="body2" sx={{ mb: 2, fontSize: '0.8rem', color: 'text.secondary' }}>
                  Upload documents with automatic "_pip" naming for PIP documentation
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <input
                    type="file"
                    id="pip-file-input"
                    multiple
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                  />

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button
                      variant="outlined"
                      onClick={() => document.getElementById('pip-file-input').click()}
                      startIcon={<AttachFileIcon fontSize="small" />}
                      disabled={uploadingFiles}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 500
                      }}
                    >
                      {uploadingFiles ? 'Uploading...' : 'Select & Upload Files'}
                    </Button>

                    {uploadingFiles && (
                      <LinearProgress sx={{ flex: 1, ml: 2 }} />
                    )}
                  </Box>

                  {selectedFiles.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 1 }}>
                        Selected files:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {selectedFiles.map((file, index) => (
                          <Chip
                            key={index}
                            label={`${file.name} → ${file.name.replace(/\.[^/.]+$/, '')}_pip.${file.name.split('.').pop()}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.75rem' }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* Uploaded Files Display - Right below the button */}
                  {attachedFiles.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                        Uploaded Documents:
                      </Typography>
                      <Stack spacing={1}>
                        {attachedFiles.map((file, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 1.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              backgroundColor: 'background.paper',
                              '&:hover': {
                                backgroundColor: 'action.hover'
                              }
                            }}
                          >
                            <AttachFileIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                {typeof file === 'string' ? file : file.name}
                              </Typography>
                              {typeof file === 'object' && (
                                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                  {file.uploadError ? 'Upload failed - check console' :
                                    `Uploaded ${file.uploadedAt ? new Date(file.uploadedAt).toLocaleString() : ''}`}
                                  {file.url && ` - URL: ${file.url.substring(0, 50)}...`}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {typeof file === 'object' && file.url && (
                                <IconButton
                                  size="small"
                                  onClick={() => window.open(file.url, '_blank')}
                                  sx={{ ml: 0.5 }}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              )}
                              <IconButton
                                size="small"
                                onClick={() => removeAttachedFile(index)}
                                color="error"
                                sx={{ ml: 0.5 }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Paper>


            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                Dynamic Fields
              </Typography>

              {Object.keys(dynamicFields).length > 0 && (
                <Paper variant="outlined" sx={{ mb: 2 }}>
                  <List dense>
                    {Object.entries(dynamicFields).map(([key, value]) => (
                      <ListItem key={key}>
                        <ListItemText
                          primary={`${key}: ${value}`}
                          primaryTypographyProps={{ fontSize: '0.875rem' }}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => removeDynamicField(key)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Field Name"
                    value={newFieldKey}
                    onChange={(e) => setNewFieldKey(e.target.value)}
                    placeholder="e.g., Target Improvement"
                    InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                    InputProps={{ sx: { fontSize: '0.875rem' } }}
                    sx={{
                      '& .MuiInputBase-input::placeholder': {
                        fontSize: '0.8rem',
                        opacity: 0.6
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Field Value"
                    value={newFieldValue}
                    onChange={(e) => setNewFieldValue(e.target.value)}
                    placeholder="Enter field value"
                    InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                    InputProps={{ sx: { fontSize: '0.875rem' } }}
                    sx={{
                      '& .MuiInputBase-input::placeholder': {
                        fontSize: '0.8rem',
                        opacity: 0.6
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    variant="outlined"
                    onClick={addDynamicField}
                    disabled={!newFieldKey.trim() || !newFieldValue.trim()}
                    startIcon={<AddIcon fontSize="small" />}
                    sx={{
                      minWidth: 100,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      height: '56px'
                    }}
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="success" icon={<CheckCircleIcon />} sx={{ fontSize: '0.875rem' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                  Review & Finalize
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  Review all information before saving the PIP plan.
                </Typography>
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    PIP Plan Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>User:</Typography>
                      <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>
                        {selectedUserName || 'Not selected'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Manager:</Typography>
                      <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>
                        {getManagerName(actualManagerId)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Assigned Lead:</Typography>
                      <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>
                        {getLeadName(actualLeadId)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Start Date:</Typography>
                      <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>{pipForm.pip_start_date}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>End Date:</Typography>
                      <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>{pipForm.pip_end_date}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Final Decision:</Typography>
                      <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>{pipForm.final_decision || 'Not specified'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Location:</Typography>
                      <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>{pipForm.location || 'Not specified'}</Typography>
                    </Grid>
                    {pipForm.release_date && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Release Date:</Typography>
                        <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>{pipForm.release_date}</Typography>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Attached Files:</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {attachedFiles.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {attachedFiles.map((file, index) => (
                              <Button
                                key={index}
                                variant="text"
                                size="small"
                                onClick={() => window.open(typeof file === 'object' ? file.url : file, '_blank')}
                                startIcon={<AttachFileIcon fontSize="small" />}
                                sx={{
                                  fontSize: '0.75rem',
                                  textTransform: 'none',
                                  p: 0.5,
                                  minHeight: 'auto',
                                  '&:hover': {
                                    backgroundColor: 'action.hover'
                                  }
                                }}
                              >
                                {typeof file === 'string' ? file : file.name}
                              </Button>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>
                            None
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    {Object.keys(dynamicFields).length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Dynamic Fields:</Typography>
                        <Box sx={{ mt: 1 }}>
                          {Object.entries(dynamicFields).map(([key, value]) => (
                            <Chip
                              key={key}
                              label={`${key}: ${value}`}
                              size="small"
                              sx={{ mr: 1, mb: 1, fontSize: '0.75rem' }}
                            />
                          ))}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <React.Fragment>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #cc4049 0%, #0a0068 100%)',
          color: 'white',
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 40, height: 40 }}>
            <AssignmentIcon sx={{ fontSize: 22, color: 'white' }} />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem', mb: 0.5 }}>
              {/* {editingPIPPlan ? 'Edit PIP Plan' : 'Create PIP Plan'} */}
              {'Create PIP Plan'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
              Performance Improvement Plan
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem', fontStyle: 'italic' }}>
              Multiple PIP plans supported per user across different review periods
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 3, fontSize: '0.875rem' }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                {error}
              </Typography>
            </Alert>
          )}

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          <Box sx={{ p: 3 }}>
            {steps.map((label, index) => (
              <Accordion
                key={label}
                expanded={expandedPanel === `panel${index}`}
                onChange={() => setExpandedPanel(expandedPanel === `panel${index}` ? null : `panel${index}`)}
                sx={{
                  mb: 1,
                  border: `1px solid ${alpha('#0a0068', 0.1)}`,
                  borderRadius: 2,
                  '&:before': { display: 'none' },
                  boxShadow: 'none'
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: alpha('#0a0068', 0.05),
                    borderRadius: '8px 8px 0 0',
                    '& .MuiAccordionSummary-content': {
                      margin: '12px 0'
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0a0068' }}>
                    {label}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2 }}>
                  {getStepContent(index)}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, bgcolor: 'rgba(0,0,0,0.02)', gap: 2 }}>
          <Button
            onClick={onClose}
            color="secondary"
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              minWidth: 120,
              borderRadius: 2
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || !isFormValid()}
            startIcon={<SaveIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 600,
              borderRadius: 2,
              minWidth: 140,
              background: 'linear-gradient(45deg, #0a0068 30%, #1a1078 90%)',
              boxShadow: '0 4px 15px rgba(10,0,104,0.3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1a1078 30%, #0a0068 90%)',
                boxShadow: '0 6px 20px rgba(10,0,104,0.4)',
              },
              '&:disabled': {
                background: 'rgba(0,0,0,0.12)',
                color: 'rgba(0,0,0,0.26)'
              }
            }}
          >
            {loading ? 'Saving...' : 'Save PIP Plan'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '80vh',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AttachFileIcon />
            <Typography variant="h6" fontWeight="bold">
              File Preview: {previewFile?.name}
            </Typography>
          </Box>
          <Button
            onClick={handleClosePreview}
            sx={{
              color: 'white',
              minWidth: 'auto',
              p: 1
            }}
          >
            ✕
          </Button>
        </DialogTitle>

        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {previewFile && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {/* File info */}
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'grey.50' }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>File:</strong> {previewFile.name}
                </Typography>
                {previewFile.uploadedAt && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Uploaded:</strong> {new Date(previewFile.uploadedAt).toLocaleString()}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  <strong>URL:</strong> {previewFile.url}
                </Typography>
              </Box>

              {/* File preview */}
              <Box sx={{ flex: 1, p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {previewFile.name?.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i) ? (
                  // Image preview
                  <img
                    src={previewFile.url}
                    alt={previewFile.name}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'block';
                    }}
                  />
                ) : previewFile.name?.toLowerCase().endsWith('.pdf') ? (
                  // PDF preview using iframe
                  <iframe
                    src={`${previewFile.url}#toolbar=1&navpanes=1&scrollbar=1`}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      minHeight: '500px'
                    }}
                    title={`Preview of ${previewFile.name}`}
                  />
                ) : (
                  // Fallback for other file types
                  <Box sx={{ textAlign: 'center', p: 4 }}>
                    <AttachFileIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Preview not available
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      This file type cannot be previewed in the browser.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => window.open(previewFile.url, '_blank')}
                      startIcon={<AttachFileIcon />}
                    >
                      Open in New Tab
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, backgroundColor: 'grey.50' }}>
          <Button
            variant="outlined"
            onClick={() => window.open(previewFile?.url, '_blank')}
            startIcon={<AttachFileIcon />}
          >
            Open in New Tab
          </Button>
          <Button
            variant="contained"
            onClick={handleClosePreview}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default PIPDialog;